import React from 'react';
import clsx from 'clsx';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

import Button from '../Button';
import Spinner from '../Spinner';
import StyledElement from '../StyledElement';
import { PAGE_TYPES } from '../../enums/page-types';
import useEffectiveColorMode from '../../hooks/useEffectiveColorMode';

export const TEST_CARD_TITLE = 'Test card';
export const TEST_CARD_OUTCOME_TEXT = 'succeeds';

export function shouldShowTestCardButton({
  isTestMode,
  showPaymentMethods,
  pageType,
  isUpsell,
  isScreenshot,
  isMeasurer,
  isUninteractivePreview,
}) {
  return (
    isTestMode === true &&
    showPaymentMethods === true &&
    pageType !== PAGE_TYPES.FORM &&
    !isUpsell &&
    !isScreenshot &&
    !isMeasurer &&
    !isUninteractivePreview
  );
}

// Like the test mode banner, the tile keeps English text and fixed colours per colour mode, so a
// merchant theme can't hide it; only the pay button follows the theme, to match the real one.
export default function TestCardPayButton({ text, disabled, isSubmitting, spinnerColor, onPay }) {
  const isDarkMode = useEffectiveColorMode() === 'dark';

  return (
    <div className="cp_test-card-wrapper">
      <div
        className={clsx(
          'cp_test-card flex items-center gap-3 rounded-md border-2 border-dashed border-chocolate-600 py-2.5 pl-3 pr-2.5',
          isDarkMode ? 'bg-[#171717] text-white' : 'bg-white text-[#0a0a0a]',
        )}
      >
        <span
          aria-hidden="true"
          className="cp_test-card-brand flex h-[30px] w-[46px] flex-none items-center justify-center rounded-[5px] border border-[#e3e3e3] bg-white text-[13px] font-black italic tracking-[-0.02em] text-[#1a1f71] [font-family:'Helvetica_Neue',Arial,sans-serif]"
        >
          VISA
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="cp_test-card-title text-[14px] leading-[18px] font-semibold">
            {TEST_CARD_TITLE}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 text-[12px] leading-[16px]">
            <span className="whitespace-nowrap tabular-nums tracking-[0.06em]">•••• 4242</span>
            <span
              className={clsx(
                'cp_test-card-outcome inline-flex items-center gap-1 whitespace-nowrap',
                isDarkMode ? 'text-[#4ade80]' : 'text-[#13804b]',
              )}
            >
              <CheckCircleIcon className="h-3.5 w-3.5 translate-y-px" aria-hidden="true" />
              {TEST_CARD_OUTCOME_TEXT}
            </span>
          </span>
        </div>

        <StyledElement
          elementPath="checkout.form.button"
          as={Button}
          className="cp_button cp_test-card-button flex-none whitespace-nowrap leading-snug !px-3.5 !py-2"
          type="button"
          appearance="submit"
          disabled={disabled}
          onClick={onPay}
          aria-label={`${text} with test card`}
        >
          {isSubmitting ? (
            <span className="inline-flex items-center justify-center h-[1lh]">
              <Spinner
                className="cp_spinner"
                width="18px"
                height="18px"
                appearance="custom"
                style={{ color: spinnerColor }}
              />
            </span>
          ) : (
            text
          )}
        </StyledElement>
      </div>
    </div>
  );
}
